import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { useAppSelector } from '../../redux/store'
import { selectUserTeamMemberInfo } from '../../redux/auth/selector'
import { Box, Grid } from '@mui/system'
import {
  Button,
  CardActionArea,
  CardMedia,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded'
import useAddFeedback from '../../hooks/feedbackHooks/useAddFeedback'
import useConfirmationAlert from '../../hooks/componentHooks/useConfirmationAlert'
import { gradientColors, textColors } from '../Common/Color/Color'
import { fontSizes } from '../Common/Typography/TypographyStyles'
import { useLoading } from '../../context/LoadingContext'
import LoadingBackdrop from '../Common/Loading/LoadingBackdrop'
import showToast from '../Common/SweetAlert/ToastAlert'
import AddIcon from '@mui/icons-material/Add'

interface CreateFeedbackProps {
  userRole?: string
  onCreate?: () => Promise<void> | void
}

const CreateFeedback: React.FC<CreateFeedbackProps> = ({ userRole, onCreate }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const [imageFile, setImageFile] = useState<File | undefined>(undefined)
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined)

  const [feedback, setFeedback] = useState('')
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()
  const { createFeedback } = useAddFeedback()

  const { loading } = useLoading()

  const [open, setOpen] = useState(false)

  const handleOpenModal = () => {
    setOpen(true)
  }

  const isSubmitButtonDisabled = feedback == '' ? true : false

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    const maxSize = 5 * 1024 * 1024 // 5 MB

    if (file) {
      if (file.size > maxSize) {
        showToast('error', 'File size must be less than 5MB!')
      } else {
        setImageFile(file)
        setPreviewUrl(URL.createObjectURL(file))
      }
    }
  }

  const clearForm = () => {
    setFeedback('')
    setImageFile(undefined)
    setPreviewUrl(undefined)
  }

  const redirectOrClose = () => {
    if (userRole === 'admin') {
      clearForm()
      setOpen(false)
    } else navigate(-1)
  }

  const handleCloseModal = () => {
    if (feedback !== '' || imageFile) {
      openConfirmationAlert({
        title: 'Discard changes',
        text: 'Are you sure you want to discard your changes?',
        onConfirm: () => {
          redirectOrClose()
        },
      })
    } else {
      redirectOrClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    openConfirmationAlert({
      title: 'Send Feedback',
      text: 'Are you sure you want to send this feedback?',
      onConfirm: async () => {
        await createFeedback({
          workdayId: teamMemberDetails.workdayId,
          firstName: teamMemberDetails.firstName,
          lastName: teamMemberDetails.lastName,
          middleName: teamMemberDetails.middleName,
          suffix: teamMemberDetails.suffix,
          email: teamMemberDetails.email,
          operationalManager: teamMemberDetails.operationalManager,
          supervisor: teamMemberDetails.supervisor,
          feedback: feedback,
          imageFileName: imageFile?.name,
          imageFile: imageFile,
        })
        await onCreate?.()
        setFeedback('')
        redirectOrClose()
      },
    })
  }

  return (
    <React.Fragment>
      {userRole === 'admin' && (
        <Button
          variant='text'
          id='create-feedback-button'
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
        >
          Create Feedback
        </Button>
      )}
      <Dialog open={userRole === 'admin' ? open : true} fullWidth fullScreen={fullScreen}>
        <DialogTitle
          sx={{
            background: gradientColors.greenGradient,
            color: textColors.white,
            fontWeight: 'bold',
            fontSize: fontSizes.size20,
          }}
        >
          Send Feedback
        </DialogTitle>
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: textColors.white,
          }}
          size='small'
          data-testid='create-feedback-close-button'
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: textColors.white,
          }}
          size='small'
          data-testid='create-feedback-close-button'
        ></IconButton>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} direction='column'>
              <Grid>
                <TextField
                  name='workdayId'
                  label='Workday ID'
                  value={teamMemberDetails.workdayId}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  name='fullName'
                  label='Full Name'
                  value={
                    teamMemberDetails.firstName +
                    ' ' +
                    teamMemberDetails.middleName +
                    ' ' +
                    teamMemberDetails.lastName
                  }
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  name='email'
                  label='TI Email'
                  value={teamMemberDetails.email}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  name='supervisor'
                  label='Supervisor'
                  value={teamMemberDetails.supervisor.name}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  name='operationalManager'
                  label='Operational Manager'
                  value={teamMemberDetails.operationalManager.name}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  name='details'
                  label='Feedback'
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder='Type your message here...'
                  multiline
                  rows={10}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                />
              </Grid>
              <Grid>
                <Box position='relative' sx={{ overflow: 'hidden' }}>
                  <CardActionArea
                    component='label'
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <input
                      id='event-image-input'
                      hidden
                      accept='image/*'
                      type='file'
                      onChange={handleFileChange}
                    />
                    {!imageFile ? (
                      <Box
                        id='event-image-box'
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed',
                          borderColor: textColors.purpleTelus,
                          color: textColors.purpleTelus,
                          padding: 2, // Padding for better spacing
                        }}
                      >
                        <AddPhotoAlternateRoundedIcon />
                        Upload Image
                      </Box>
                    ) : (
                      <CardMedia
                        component='img'
                        image={previewUrl}
                        alt='event-card-preview'
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          zIndex: 0,
                        }}
                      />
                    )}
                  </CardActionArea>
                </Box>
              </Grid>
              <Grid>
                <Button type='submit' variant='contained' disabled={isSubmitButtonDisabled}>
                  Submit
                </Button>
              </Grid>
              {ConfirmationAlert}
            </Grid>
          </form>
        </DialogContent>
        {loading && <LoadingBackdrop message={'Sending feedback...'} />}
      </Dialog>
    </React.Fragment>
  )
}

export default CreateFeedback
